import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  downloadCapability, fetchCapabilityDetails, fetchPushRepos, fetchPushTargets, pushCapability,
} from '../../services/capabilitiesApi';
import type { CapabilityCatalogItem, CapabilityDetails as Details, PushRepo, PushTargets } from '../../services/capabilitiesApi';
import { Action, Chip, Field, Input, Kicker, Loading, Modal, Select } from '../aa';
import { ManageInWorkspace } from '../settings/ManageInWorkspace';
import { relativeTime } from '../../pages/partner/format';

/*
 * One skill or plugin, in full.
 *
 * What it is for (the description the agent reads to decide when to use it),
 * where it came from, the files it is made of, and two ways to take it out:
 * download the folder as a zip, or push it into a repository. Pushing is the
 * importer's alone: with the company's own GitHub or GitLab connected they
 * pick any repository; without one, Agents Anywhere keeps one per company.
 */

type Provider = 'github' | 'gitlab' | 'managed';

const FORMAT_LABEL: Record<string, string> = {
  claude: 'Claude Code',
  codex: 'Codex',
  agentskills: 'Agent Skills',
  agentsanywhere: 'Agents Anywhere',
};

const PROVIDER_LABEL: Record<Provider, string> = { github: 'GitHub', gitlab: 'GitLab', managed: 'Agents Anywhere' };

const detailOf = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || fallback;

const sourceLine = (d: Details): string => {
  if (d.sourceType === 'github' && d.sourceRepo) return `Imported from ${d.sourceRepo}${d.sourcePath ? ` (${d.sourcePath})` : ''}`;
  if (d.sourceType === 'platform' || d.visibility === 'PLATFORM') return 'Comes with Agents Anywhere';
  if (!d.ownedByWorkspace && d.visibility === 'PUBLIC') return `Published by ${d.authorName || 'a partner'}`;
  return `Created here${d.authorName ? ` by ${d.authorName}` : ''}`;
};

const folderFor = (d: Details): string => {
  if (d.kind === 'plugin') return `plugins/${d.slug}`;
  if (d.kind === 'template') return `templates/${d.slug}`;
  return `skills/${d.slug}`;
};

const PushPanel: React.FC<{ clientId: string; details: Details; onPushed: () => void }> = ({ clientId, details, onPushed }) => {
  const [targets, setTargets] = useState<PushTargets | null>(null);
  const [provider, setProvider] = useState<Provider | null>(null);
  const [repos, setRepos] = useState<PushRepo[]>([]);
  const [repo, setRepo] = useState('');
  const [path, setPath] = useState(folderFor(details));
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPushTargets(clientId)
      .then((t) => {
        setTargets(t);
        if (t.github.connected) setProvider('github');
        else if (t.gitlab.connected) setProvider('gitlab');
        else setProvider('managed');
      })
      .catch((err) => setError(detailOf(err, 'Could not read where this can be pushed.')));
  }, [clientId]);

  useEffect(() => {
    if (provider !== 'github' && provider !== 'gitlab') return;
    setLoadingRepos(true);
    setRepos([]);
    setRepo('');
    fetchPushRepos(clientId, provider)
      .then((list) => {
        setRepos(list);
        if (list[0]) setRepo(list[0].fullName);
      })
      .catch((err) => setError(detailOf(err, 'Could not list your repositories.')))
      .finally(() => setLoadingRepos(false));
  }, [clientId, provider]);

  if (error) return <p className="m-0 text-[12.5px] text-amber-700">{error}</p>;
  if (!targets || !provider) return <Loading label="Checking where this can go" />;

  const ownConnected = targets.github.connected || targets.gitlab.connected;
  const providers: Provider[] = [];
  if (targets.github.connected) providers.push('github');
  if (targets.gitlab.connected) providers.push('gitlab');

  const push = async () => {
    setBusy(true);
    try {
      const chosen = repos.find((r) => r.fullName === repo);
      const result = await pushCapability(clientId, {
        capabilityId: details.id,
        provider,
        repoFullName: provider === 'managed' ? undefined : repo,
        path: path.trim() || undefined,
        connectionId: chosen?.connectionId || undefined,
      });
      toast.success(`${details.name} is in ${result.repoFullName}.`);
      onPushed();
    } catch (err) {
      toast.error(detailOf(err, 'The push did not go through.'));
    } finally {
      setBusy(false);
    }
  };

  if (!ownConnected) {
    return (
      <div className="space-y-3">
        <p className="m-0 text-[12.5px] leading-[20px] text-ink-dim">
          Your company has no GitHub or GitLab connected, so Agents Anywhere keeps one private repository for your company
          in its <span className="font-semibold text-ink">{targets.managed.org}</span> organisation. Connect your own under
          Code &amp; ownership in the Workspace and new pushes go to a repository you choose.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Action disabled={busy || !targets.managed.available} reason={targets.managed.available ? undefined : 'Not available right now'} onClick={push}>
            Push to the Agents Anywhere repository
          </Action>
          <ManageInWorkspace
            label="Connect your own"
            className="!h-10 !rounded-act !px-4 !text-[13px] hover:!border-line hover:!bg-canvas-inset"
            row={{ slug: 'github', label: 'GitHub', category: 'code', category_label: 'Code', enabled: true, connect_here: false, personal_status: null, organization_status: null }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        {providers.length > 1 && (
          <Field label="Where" htmlFor="cap-push-provider">
            <Select id="cap-push-provider" value={provider} onChange={(e) => setProvider(e.target.value as Provider)}>
              {providers.map((p) => <option key={p} value={p}>{PROVIDER_LABEL[p]}</option>)}
            </Select>
          </Field>
        )}
        <Field label="Repository" htmlFor="cap-push-repo" hint={loadingRepos ? 'Loading your repositories' : undefined}>
          <Select id="cap-push-repo" value={repo} onChange={(e) => setRepo(e.target.value)} disabled={loadingRepos || repos.length === 0}>
            {repos.length === 0 && <option value="">No repositories found</option>}
            {repos.map((r) => <option key={r.fullName} value={r.fullName}>{r.fullName}</option>)}
          </Select>
        </Field>
        <Field label="Folder" htmlFor="cap-push-path" className={providers.length > 1 ? 'sm:col-span-2' : ''}>
          <Input id="cap-push-path" value={path} onChange={(e) => setPath(e.target.value)} />
        </Field>
      </div>
      <Action disabled={busy || !repo} reason={repo ? undefined : 'Pick a repository'} onClick={push}>
        Push to {PROVIDER_LABEL[provider]}
      </Action>
    </div>
  );
};

type DetailsItem = CapabilityCatalogItem & {
  providedBy?: 'aa' | 'company' | 'mine';
  ownerEmail?: string | null;
  isMine?: boolean;
};

const PROVIDED_LABEL = { aa: 'Agents Anywhere', company: 'Company', mine: 'Mine' } as const;

const providedBy = (item: DetailsItem): 'aa' | 'company' | 'mine' => {
  if (item.providedBy) return item.providedBy;
  if (item.origin === 'agents_anywhere') return 'aa';
  return 'company';
};

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="rounded-act border border-line-soft bg-canvas px-3.5 py-3">
    <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-soft">{label}</div>
    <div className="mt-1 break-words text-[13px] leading-[20px] text-ink">{children}</div>
  </div>
);

const Section: React.FC<{ title: string; aside?: React.ReactNode; children: React.ReactNode }> = ({ title, aside, children }) => (
  <section>
    <div className="mb-2 flex items-center justify-between gap-3">
      <div className="text-[12px] font-semibold uppercase tracking-[0.08em] text-ink-soft">{title}</div>
      {aside}
    </div>
    {children}
  </section>
);

export const CapabilityDetails: React.FC<{
  clientId: string;
  item: DetailsItem;
  canPush: boolean;
  onClose: () => void;
}> = ({ clientId, item, canPush, onClose }) => {
  const [details, setDetails] = useState<Details | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reading, setReading] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const load = () => {
    fetchCapabilityDetails(clientId, item.id)
      .then(setDetails)
      .catch((err) => setError(detailOf(err, 'Could not load this one.')));
  };
  useEffect(load, [clientId, item.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async () => {
    setDownloading(true);
    try {
      await downloadCapability(clientId, item.id, item.slug);
    } catch (err) {
      toast.error(detailOf(err, 'Could not download it.'));
    } finally {
      setDownloading(false);
    }
  };

  const kindLabel = item.kind === 'plugin' ? 'Plugin' : 'Skill';
  const provided = providedBy(item);
  const owner = details?.ownerEmail || item.ownerEmail || null;
  const fileCount = details ? details.files.length : 0;
  const open = details?.files.find((f) => f.path === reading);
  let readingText = '';
  if (open) readingText = open.content;
  let pushNote = 'Only the person who imported it can push it to a repository.';
  if (owner) pushNote = `Only ${owner}, who imported it, can push it to a repository.`;

  return (
    <Modal
      size="dialog"
      onClose={onClose}
      context={<Kicker>{kindLabel}</Kicker>}
      badge={(
        <>
          <Chip tone={provided === 'mine' ? 'ok' : 'neutral'}>{PROVIDED_LABEL[provided]}</Chip>
          {provided === 'company' && owner && <span className="text-[12px] text-ink-dim">Shared by {owner}</span>}
          {item.origin === 'partner' && <Chip>by {item.authorName || 'partner'}</Chip>}
        </>
      )}
      footer={(
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Action variant="quiet" onClick={download} disabled={downloading || !details} reason="Preparing">Download zip</Action>
          <Action variant="ghost" onClick={onClose}>Close</Action>
        </div>
      )}
    >
      <h2 className="m-0 text-[24px] font-bold leading-[31px] tracking-[-0.015em] text-ink">{item.name}</h2>
      <p className="m-0 mt-2 text-[14px] leading-[23px] text-ink-muted">{item.description || details?.description || 'No description yet.'}</p>
      {!details && !error && <div className="mt-6"><Loading label={`Loading the ${kindLabel.toLowerCase()}`} /></div>}
      {error && <p className="m-0 mt-6 text-[13px] text-amber-700">{error}</p>}
      {details && (
        <div className="mt-6 space-y-6">
          <div className="grid gap-2.5 sm:grid-cols-2">
            <Fact label="Where it came from">{sourceLine(details)}</Fact>
            <Fact label="Format">{FORMAT_LABEL[details.sourceFormat || ''] || 'Agents Anywhere'} · v{details.version || '1.0.0'}</Fact>
            <Fact label="Needs connected">{details.requiresApps.length > 0 ? details.requiresApps.join(', ') : 'Nothing'}</Fact>
            <Fact label="Last pushed">
              {details.push && (
                <>
                  {details.push.repoFullName} · {relativeTime(details.push.at)}{' '}
                  <a className="font-semibold text-ink underline underline-offset-2" href={details.push.url} target="_blank" rel="noreferrer">Open</a>
                </>
              )}
              {!details.push && 'Not pushed to a repository yet'}
            </Fact>
          </div>

          <Section title={`Files · ${fileCount}`}>
            <ul className="m-0 list-none divide-y divide-line-soft rounded-act border border-line p-0">
              {details.files.map((f) => {
                const isOpen = reading === f.path;
                let toggle = 'Read';
                if (isOpen) toggle = 'Hide';
                return (
                  <li key={f.path} className="flex items-center justify-between gap-3 px-3.5 py-2.5 text-[13px] text-ink">
                    <span className="truncate font-mono text-[12.5px]">{f.path}</span>
                    <button type="button" className="shrink-0 text-[12px] font-semibold text-ink underline underline-offset-2" onClick={() => setReading(isOpen ? null : f.path)}>{toggle}</button>
                  </li>
                );
              })}
              {fileCount === 0 && <li className="px-3.5 py-2.5 text-[13px] text-ink-dim">No files yet.</li>}
            </ul>
            {open && (
              <pre className="mt-3 max-h-[320px] overflow-auto whitespace-pre-wrap break-words rounded-act border border-line bg-canvas p-4 text-[12px] leading-relaxed text-ink">{readingText || 'This file is empty.'}</pre>
            )}
          </Section>

          {provided !== 'aa' && (
            <Section title="Push to a repository">
              {canPush && <PushPanel clientId={clientId} details={details} onPushed={load} />}
              {!canPush && <p className="m-0 text-[12.5px] leading-[20px] text-ink-dim">{pushNote}</p>}
            </Section>
          )}
        </div>
      )}
    </Modal>
  );
};

export default CapabilityDetails;
